/**
 * True fisheye rendering for the onboard camera views (ADR-023).
 *
 * WHY THIS EXISTS. A real-time engine draws a pinhole (rectilinear)
 * projection: a point at angle theta off the optical axis lands at
 * r = f * tan(theta). That model cannot represent a fisheye. At 180 deg
 * tan(theta/2) diverges, and well before that — past roughly 120 deg — the
 * frame edges stretch so badly that the image is useless. The robot's CU21 /
 * CU22 bodies carry fisheye lenses, so rendering them with a perspective
 * camera would not merely look wrong, it would produce images with the wrong
 * geometry for every pixel. A perception model trained on that does not
 * transfer to the real camera.
 *
 * HOW. The scene is rendered once into a cube map centred on the camera, and
 * a fullscreen shader then samples that cube along the direction each output
 * pixel corresponds to under the chosen fisheye projection. The cube map is
 * projection-agnostic — it is just "the light arriving at this point" — so
 * swapping equidistant for equisolid, or changing the FOV, is a uniform
 * change and never a re-render.
 *
 * COST. Six scene renders per frame instead of one. This is an engineering /
 * data-generation view, never the participant experience, so the trade is
 * deliberate — and for offline capture the face resolution is raised further
 * still (see CUBE_FACE_TIERS), because a dataset frame is rendered once and
 * lived with forever, while a dropped interactive frame is forgotten.
 */

import { Effect } from "@babylonjs/core/Materials/effect";
import { Matrix, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { PostProcess } from "@babylonjs/core/PostProcesses/postProcess";
import { ReflectionProbe } from "@babylonjs/core/Probes/reflectionProbe";
import { Camera } from "@babylonjs/core/Cameras/camera";
import type { Scene } from "@babylonjs/core/scene";
import type { LensProfile } from "../core/cameraFrame";
import { PROJECTION_CODE, maxTheta, normalizedFocal } from "../core/cameraFrame";

/**
 * Cube face resolution tiers.
 *
 * The cube face is the HARD limit on how sharp a fisheye frame can be, and it
 * is easy to under-provision. A face spans exactly 90 deg, so it carries
 * `facePx / 90` pixels per degree; the output frame carries `outWidthPx /
 * fov_h_deg`. At the shipped 102.69 deg lens a 1920-wide frame wants 18.7
 * px/deg, which a 512 px face (5.7 px/deg) cannot supply — the image is then
 * ~3x undersampled and reads as soft no matter what else is done to it.
 *
 *   play  512  — six renders stay affordable on a phone GPU (interactive)
 *   hq   1024  — enough for a 1280x720 capture (11.4 px/deg)
 *   max  2048  — enough for 1920x1080 (22.8 px/deg), the dataset default
 */
export const CUBE_FACE_TIERS = { play: 512, hq: 1024, max: 2048 } as const;
export type OpticsQuality = keyof typeof CUBE_FACE_TIERS;

/** Default cube face resolution for the interactive view. */
const CUBE_FACE_PX = CUBE_FACE_TIERS.play;

export interface FisheyeOptions {
  /** Cube face resolution in pixels. See CUBE_FACE_TIERS. */
  cubeFacePx?: number;
}

/**
 * NO MSAA HERE, AND IT IS NOT AN OVERSIGHT.
 *
 * The default framebuffer's antialiasing does nothing for this view — the only
 * geometry drawn to it is a fullscreen quad, and every real edge lives inside
 * the cube map — so multisampling the cube looks like the obvious fix. It is
 * not: setting `probe.cubeTexture.samples = 4` on Babylon 8.56.2 / WebGL2
 * leaves every field reporting success (samples reads back as 4, the probe
 * keeps its render list, the post-process reports ready) and renders NOTHING.
 * The frame comes back as the scene's clear colour, silently, with no warning
 * in the console. Measured on Chromium 2026-09, cube faces at 1024 and 2048.
 *
 * Antialias the OUTPUT instead: render the fisheye pass into an oversized
 * drawing buffer (engine.setHardwareScalingLevel(1 / ss)) and let the
 * downscale average it. That pass is one fullscreen quad, so the cost is
 * negligible next to the six scene renders — see scripts/capture_clip.mjs
 * --ss.
 */

/** The exact numbers the shader is using — written into clip metadata so the
 * geometry of a captured frame is reproducible outside this repo. */
export interface OpticsDescription {
  /** Lens profile name as resolved, so a clip can never disagree with it. */
  lens: string;
  projection: string;
  fov_h_deg: number;
  status: string;
  /** Focal length in units where the sensor half-height is 1. */
  focal_norm: number;
  /** Half-FOV at the sensor corner (rad); past this the frame is void. */
  max_theta_rad: number;
  sensor_aspect: number;
  cube_face_px: number;
  /** Always 1 — see the note above attachFisheye. */
  cube_msaa: number;
}

const FRAGMENT = `
precision highp float;
varying vec2 vUV;

uniform samplerCube cubeTex;
uniform mat4 camRot;        // camera world rotation (translation stripped)
uniform vec4 vpRect;        // this camera's viewport in framebuffer pixels
uniform vec2 sensorScale;   // viewport-normalized -> sensor units
                            // (NOT named "scale": Babylon's default
                            //  post-process vertex shader already owns
                            //  that name and uses it to build vUV)
uniform vec2 halfSensor;    // sensor half-width/half-height = (aspect, 1)
uniform float focal;        // normalized focal length: r = focal * g(theta)
uniform float maxTheta;     // half-FOV along the sensor diagonal (rad)
uniform int projModel;      // 0 equidistant, 1 equisolid, 2 rectilinear
uniform vec3 voidColor;

void main(void) {


  // Sensor-plane coordinates, origin at the optical centre. The rendered
  // frame keeps the REAL sensor's aspect ratio and is letterboxed into
  // whatever viewport it is shown in, so what you see is the frame the
  // camera would actually capture rather than a crop of it.
  // Derive the tile-local coordinate from gl_FragCoord and the camera's own
  // viewport rather than from vUV. vUV's span depends on whether the pass is
  // fullscreen or viewport-bound, which differs between the solo and tiled
  // views; gl_FragCoord is always framebuffer pixels, so this is unambiguous.
  vec2 local = (gl_FragCoord.xy - vpRect.xy) / vpRect.zw;
  vec2 p = (local - 0.5) * 2.0 * sensorScale;
  if (abs(p.x) > halfSensor.x || abs(p.y) > halfSensor.y) {
    gl_FragColor = vec4(voidColor, 1.0);
    return;
  }

  float r = length(p);
  float theta;
  if (projModel == 0) {
    theta = r / focal;                                   // equidistant  r = f*theta
  } else if (projModel == 1) {
    theta = 2.0 * asin(clamp(r / (2.0 * focal), -1.0, 1.0)); // equisolid r = 2f*sin(theta/2)
  } else {
    theta = atan(r / focal);                             // rectilinear  r = f*tan(theta)
  }

  // Beyond the lens's own limit there is simply no image.
  if (theta > maxTheta) {
    gl_FragColor = vec4(voidColor, 1.0);
    return;
  }

  // Direction in camera space: +X right, +Y up, +Z forward (Babylon).
  vec2 dir2 = r > 1e-6 ? p / r : vec2(0.0, 0.0);
  vec3 dirCam = vec3(sin(theta) * dir2.x, sin(theta) * dir2.y, cos(theta));
  vec3 dirWorld = normalize((camRot * vec4(dirCam, 0.0)).xyz);

  // Babylon renders a reflection probe with _invertYAxis = false, which makes
  // it draw the DOWNWARD view into the cube face that textureCube treats as
  // +Y (and vice versa) — the engine compensates for that elsewhere when the
  // cube is consumed as a material's reflectionTexture. Sampling it directly,
  // as here, must undo the swap or the whole image comes out upside down.
  gl_FragColor = textureCube(cubeTex, vec3(dirWorld.x, -dirWorld.y, dirWorld.z));
}
`;

Effect.ShadersStore["ssFisheyeFragmentShader"] = FRAGMENT;

export interface FisheyeRig {
  /** Call once per frame, after the camera's world matrix is up to date. */
  update(): void;
  dispose(): void;
  /** Refresh the cube map's mesh list after the level is rebuilt. */
  setRenderList(meshes: Scene["meshes"]): void;
  /** The optics actually in force, for clip metadata. */
  describe(): OpticsDescription;
}


/**
 * Attach a fisheye rig to `camera`. The camera keeps its pose (the real
 * extrinsics); only the projection changes.
 */
export function attachFisheye(
  scene: Scene,
  camera: Camera,
  lens: LensProfile,
  sensorAspect: number,
  opts: FisheyeOptions = {},
): FisheyeRig {
  // The intermediate cube faces are 90-degree pinholes. Babylon's Gaussian
  // material computes its covariance focal length from activeCamera.fov,
  // while ReflectionProbe supplies a separate 90-degree projection matrix.
  // Leaving the camera's default 0.8 rad makes splats 2.365x too large and
  // covers low sensor views in a grey veil. Keep both intermediate projections
  // consistent; the final sensor lens/FOV still comes solely from the fisheye
  // remap below, with the surveyed camera pose unchanged (ADR-149).
  camera.fov = Math.PI / 2;
  camera.fovMode = Camera.FOVMODE_VERTICAL_FIXED;
  const cubeFacePx = Math.max(128, Math.round(opts.cubeFacePx ?? CUBE_FACE_PX));
  const probe = new ReflectionProbe(`onboardProbe_${camera.name}`, cubeFacePx, scene, false);
  probe.refreshRate = 1; // every frame — the robot is always moving
  // An EXPLICIT render list is required, not null. A null list makes the
  // render target draw scene.getActiveMeshes() — the meshes that survived
  // frustum culling for whichever camera happens to be active — so the cube
  // map would silently inherit another camera's field of view and lose
  // everything outside it. A fisheye sees far more than any frustum; it must
  // be handed the whole scene.
  probe.renderList = scene.meshes.slice();
  // Babylon only renders a render target that something gathers each frame —
  // normally a material referencing it. This cube is sampled by a post-process
  // instead, so nothing would gather it and it would stay at the clear colour.
  //
  // Register it on the CAMERA, not on the scene. Scene.customRenderTargets is
  // only gathered by the frame-graph render path; the ordinary path collects
  // `camera.customRenderTargets` for the camera it is about to render. With a
  // single camera the distinction is invisible, but as soon as several cameras
  // share the frame (the tiled comparison views) a scene-level registration
  // renders nothing at all — an empty cube map, and an image of pure sky.
  camera.customRenderTargets.push(probe.cubeTexture);
  // Also bind the target to this camera so its near/far planes come from the
  // onboard camera rather than whichever camera happens to be current.
  probe.cubeTexture.activeCamera = camera;

  const focal = normalizedFocal(lens, sensorAspect);
  const thetaMax = maxTheta(lens, sensorAspect);

  // Fit the sensor rectangle inside the viewport without distorting it.
  const post = new PostProcess(
    "ssFisheye",
    "ssFisheye",
    ["camRot", "vpRect", "sensorScale", "halfSensor", "focal", "maxTheta", "projModel", "voidColor"],
    ["cubeTex"],
    1.0,
    camera,
  );
  // Without this the fullscreen quad ignores the camera's viewport and each
  // tile would paint over the whole frame.
  post.forceFullscreenViewport = false;

  const rot = Matrix.Identity();
  const engine = scene.getEngine();
  const voidColor = new Vector3(0.06, 0.06, 0.07);

  post.onApply = (effect) => {
    rot.copyFrom(camera.getWorldMatrix());
    rot.setTranslation(Vector3.Zero());

    // Letterbox. The sensor's aspect is fixed by the hardware (2048x1280);
    // the viewport is whatever the phone happens to be. Fit the sensor
    // rectangle inside the viewport without ever distorting it, so the frame
    // on screen is the frame the camera captures, not a crop of it.
    // Use the CAMERA's viewport, not the whole canvas: in the tiled
    // comparison views each camera owns only a slice of the frame, and
    // letterboxing against the canvas would distort every tile.
    const vp = camera.viewport;
    // getRenderWidth() without `useScreen` reports the currently bound render
    // target — which, while these post-processes apply, is the 512 px cube
    // face, not the canvas. Passing true asks for the drawing buffer instead.
    const fbW = engine.getRenderWidth(true);
    const fbH = engine.getRenderHeight(true);
    const vw = fbW * vp.width;
    const vh = Math.max(1, fbH * vp.height);
    const viewAspect = vw / vh;
    const sx = viewAspect >= sensorAspect ? viewAspect : sensorAspect;
    const sy = viewAspect >= sensorAspect ? 1 : sensorAspect / viewAspect;

    effect.setTexture("cubeTex", probe.cubeTexture);
    effect.setMatrix("camRot", rot);
    effect.setFloat4("vpRect", fbW * vp.x, fbH * vp.y, vw, vh);
    effect.setFloat2("sensorScale", sx, sy);
    effect.setFloat2("halfSensor", sensorAspect, 1);
    effect.setFloat("focal", focal);
    effect.setFloat("maxTheta", thetaMax);
    effect.setInt("projModel", PROJECTION_CODE[lens.projection]);
    effect.setVector3("voidColor", voidColor);
  };

  return {
    update(): void {
      // Re-centre the cube on the camera — and force the transforms first.
      //
      // `camera.globalPosition` is only refreshed when the camera's view
      // matrix is computed, which happens INSIDE scene.render(). This runs
      // before that, and the onboard camera is parented to the robot whose
      // pose was set moments ago, so reading it unforced hands back LAST
      // frame's position: the cube map is then rendered from where the robot
      // was, while the shader reprojects it with where the robot is. The
      // mismatch changes every frame — that is the judder in the recordings.
      const parent = camera.parent as { computeWorldMatrix?: (force: boolean) => unknown } | null;
      parent?.computeWorldMatrix?.(true);
      camera.getViewMatrix(true);
      probe.position.copyFrom(camera.globalPosition);
    },
    setRenderList(meshes): void {
      probe.renderList = meshes.slice();
    },
    describe(): OpticsDescription {
      return {
        lens: lens.name,
        projection: lens.projection,
        fov_h_deg: lens.fov_h_deg,
        status: lens.status,
        focal_norm: focal,
        max_theta_rad: thetaMax,
        sensor_aspect: sensorAspect,
        cube_face_px: cubeFacePx,
        cube_msaa: 1,
      };
    },
    dispose(): void {
      const i = camera.customRenderTargets.indexOf(probe.cubeTexture);
      if (i >= 0) camera.customRenderTargets.splice(i, 1);
      post.dispose();
      probe.dispose();
    },
  };
}
